import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { apiRoutes } from 'config/api/apiRoutes';
import { useOrderApi } from './ordersApi';
import { IOrderSettingsFormData, ISendInvoiceRequestData, IOrderFormData } from 'models/ordersModel';

export const useGetOrdersList = (pageSize: number, languageId: string | undefined) => {
  const { getOrdersList } = useOrderApi();

  const query = useInfiniteQuery([apiRoutes.getOrders, languageId],
    ({ pageParam = 0 }) => getOrdersList(pageParam, pageSize, languageId), {
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = (lastPage.items.length === pageSize && lastPage.hasNextPage ) ? lastPage.pageIndex + 1 : undefined;
      return nextPage;
    },
  });

  return query;
}

export const useGetOrderSettings = (languageCode: string) => {
  const { getOrderSettings } = useOrderApi();

  const query = useQuery([apiRoutes.getOrderSettings, languageCode], () => getOrderSettings(languageCode), { staleTime: 0 });

  return query;
};

export const useUpdateOrderSettings = () => {
  const { updateOrderSettings } = useOrderApi();
  const queryClient = useQueryClient();

  const mutation = useMutation((data: { orderSettings: IOrderSettingsFormData }) => updateOrderSettings(data.orderSettings), {
    onSuccess: () => {
      queryClient.invalidateQueries([apiRoutes.getOrderSettings])
    },
  });

  return mutation;
}
export const useSendOrderInvoice = () => {
  const { sendOrderInvoice } = useOrderApi();

  const mutation = useMutation((data: ISendInvoiceRequestData) => sendOrderInvoice(data));

  return mutation;
};

export const useGetStatusListByOrderId = (languageCode: string) => {

  const { getStatusList } = useOrderApi();

  const query = useQuery([apiRoutes.getOrderStatusLookup, languageCode], () => getStatusList(languageCode),
    { staleTime: Infinity });

  return query

}

export const useGetOrderById = (id: number, languageCode: string) => {

  const { getOrderById } = useOrderApi();

  const query = useQuery([apiRoutes.getOrderById, id, languageCode], () => getOrderById(id, languageCode),
    { staleTime: Infinity, enabled: false });

  return query;
}

export const useUpdateOrder = () => {
  const { updateOrder } = useOrderApi();

  const queryClient = useQueryClient();

  const mutation = useMutation([apiRoutes.getOrders], (data: {
    id: number, data: IOrderFormData, isNotify: boolean, isStatusChanged: boolean, notificationPhoneNumber?: string, phoneNumber?: string, waTemplateId?: number
      , templateName?: string, templateLanguage?: string
  }) => updateOrder(data.id, data.data, data.isNotify, data.isStatusChanged, data.notificationPhoneNumber, data.phoneNumber, data.waTemplateId, data.templateName, data.templateLanguage), {
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([apiRoutes.getOrders])
    },
  })
  return mutation;
}

export const useExportOrdersToCsvFile = (languageCode: string) => {

  const { exportOrdersToCsv } = useOrderApi();

  const query = useQuery([apiRoutes.exportOrders, languageCode], () => exportOrdersToCsv(languageCode),
    { staleTime: Infinity, enabled: false, retry: false });

  return query;
}